import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const inputCls =
  "w-full rounded-xl bg-paper/70 border border-slate-200 px-4 py-3 text-[15px] text-ink placeholder:text-slate-400 focus:bg-white focus:border-signal focus:outline-none focus:ring-4 focus:ring-signal/10 transition-colors";

const FREIGHT_TYPES = [
  "Port Drayage",
  "Intermodal & Rail",
  "Full Truckload",
  "LTL",
  "Refrigerated",
  "Flatbed & Heavy Haul",
  "Warehousing",
  "Expedited",
  "Other",
];

const EQUIPMENT = [
  "53' Dry Van",
  "53' Reefer",
  "48'/40' Flatbed",
  "Step Deck",
  "Container Chassis (20'/40')",
  "Box Truck / Sprinter",
  "Not sure — advise me",
];

function Field({ label, children, required, error, testid }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-bold text-ink mb-2">
        {label} {required && <span className="text-signal">*</span>}
      </span>
      {children}
      {error && (
        <span data-testid={`${testid}-error`} className="block mt-1.5 text-xs font-semibold text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}

function useSubmit(path, refPrefix) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (payload) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}${path}`, payload, { timeout: 20000 });
      setDone(data);
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
      toast.success(`${refPrefix} request received — ref ${data.ref}`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again or email us directly.");
      return null;
    } finally {
      setLoading(false);
    }
  };
  return { loading, done, submit };
}

function SuccessCard({ done, title, body }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="card p-10 text-center border border-emerald-100"
      data-testid="form-success-card"
    >
      <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
      <h3 className="mt-4 font-display text-2xl font-bold text-ink">{title}</h3>
      <p className="mt-2 text-slate-600">{body}</p>
      {done?.ref && (
        <p className="mt-5 inline-block rounded-full bg-paper px-5 py-2 font-display text-sm font-semibold text-ink">
          Reference: <span data-testid="form-success-ref" className="text-signal">{done.ref}</span>
        </p>
      )}
    </motion.div>
  );
}

/* ============ Quote form (3 steps) ============ */

const STEPS = ["Your route", "Your details", "Review & send"];

export function QuoteForm() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    origin: "", destination: "", ready_date: "", freight_type: "", equipment: "",
    weight: "", pallets: "", name: "", company: "", email: "", phone: "", notes: "",
  });
  const [errors, setErrors] = useState({});
  const { loading, done, submit } = useSubmit("/quotes", "Quote");

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const validateStep = () => {
    const er = {};
    if (step === 0) {
      if (!f.origin.trim()) er.origin = "Pickup city or ZIP is required";
      if (!f.destination.trim()) er.destination = "Delivery city or ZIP is required";
      if (!f.freight_type) er.freight_type = "Choose a freight type";
    }
    if (step === 1) {
      if (!f.name.trim()) er.name = "Your name is required";
      if (!/^\S+@\S+\.\S+$/.test(f.email)) er.email = "Enter a valid email";
      if (f.phone.replace(/\D/g, "").length < 7) er.phone = "Enter a valid phone";
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 2)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = async () => {
    if (!validateStep()) return;
    await submit(f);
  };

  if (done) {
    return (
      <SuccessCard
        done={done}
        title="Quote request received"
        body={`We've sent it straight to our dispatch desk. Rates for your ${f.origin} → ${f.destination} lane land at ${f.email} shortly.`}
      />
    );
  }

  return (
    <div className="card p-6 sm:p-10" data-testid="quote-form">
      {/* progress */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-signal" : "bg-slate-200"} transition-colors`} />
            <p className={`mt-2.5 text-xs font-bold ${i <= step ? "text-ink" : "text-slate-400"}`}>
              {i + 1}. {s}
            </p>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Pickup city / ZIP" required error={errors.origin} testid="quote-origin">
                <input data-testid="quote-origin-input" className={inputCls} placeholder="e.g. Long Beach, 90802" value={f.origin} onChange={set("origin")} />
              </Field>
              <Field label="Delivery city / ZIP" required error={errors.destination} testid="quote-destination">
                <input data-testid="quote-destination-input" className={inputCls} placeholder="e.g. Fresno, 93706" value={f.destination} onChange={set("destination")} />
              </Field>
              <Field label="Freight type" required error={errors.freight_type} testid="quote-freight-type">
                <select data-testid="quote-freight-type-select" className={inputCls} value={f.freight_type} onChange={set("freight_type")}>
                  <option value="">Select a type…</option>
                  {FREIGHT_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Equipment" testid="quote-equipment">
                <select data-testid="quote-equipment-select" className={inputCls} value={f.equipment} onChange={set("equipment")}>
                  <option value="">Select equipment…</option>
                  {EQUIPMENT.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Ready date" testid="quote-ready-date">
                <input data-testid="quote-ready-date-input" type="date" className={inputCls} value={f.ready_date} onChange={set("ready_date")} />
              </Field>
              <div className="grid grid-cols-2 gap-5">
                <Field label="Weight (lbs)" testid="quote-weight">
                  <input data-testid="quote-weight-input" className={inputCls} placeholder="42,000" value={f.weight} onChange={set("weight")} />
                </Field>
                <Field label="Pallets / pcs" testid="quote-pallets">
                  <input data-testid="quote-pallets-input" className={inputCls} placeholder="24" value={f.pallets} onChange={set("pallets")} />
                </Field>
              </div>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name" required error={errors.name} testid="quote-name">
                <input data-testid="quote-name-input" className={inputCls} placeholder="Jordan Alvarez" value={f.name} onChange={set("name")} />
              </Field>
              <Field label="Company" testid="quote-company">
                <input data-testid="quote-company-input" className={inputCls} placeholder="Valley Foods Inc." value={f.company} onChange={set("company")} />
              </Field>
              <Field label="Email" required error={errors.email} testid="quote-email">
                <input data-testid="quote-email-input" type="email" className={inputCls} placeholder="you@company.com" value={f.email} onChange={set("email")} />
              </Field>
              <Field label="Phone" required error={errors.phone} testid="quote-phone">
                <input data-testid="quote-phone-input" type="tel" className={inputCls} placeholder="(555) 555-5555" value={f.phone} onChange={set("phone")} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Anything else we should know?" testid="quote-notes">
                  <textarea data-testid="quote-notes-input" rows={4} className={`${inputCls} resize-y`} placeholder="Dock hours, temp requirements, appointment needs, commodity…" value={f.notes} onChange={set("notes")} />
                </Field>
              </div>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="quote-review">
              {[
                ["Route", `${f.origin} → ${f.destination}`],
                ["Freight type", f.freight_type],
                ["Equipment", f.equipment || "—"],
                ["Ready date", f.ready_date || "Flexible"],
                ["Weight", f.weight || "—"],
                ["Pallets / pcs", f.pallets || "—"],
                ["Contact", f.name],
                ["Email", f.email],
                ["Phone", f.phone],
                ["Notes", f.notes || "—"],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-slate-100 pb-3">
                  <dt className="label text-[10px] text-slate-400">{k}</dt>
                  <dd className="mt-1 text-[15px] text-ink font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        {step > 0 ? (
          <button onClick={back} data-testid="quote-form-back-button" className="btn-pill text-ink border border-ink/15 hover:border-signal hover:text-corpblue px-6 py-3">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : <span />}
        {step < 2 ? (
          <button onClick={next} data-testid="quote-form-next-button" className="btn-pill primary-action px-8 py-3.5 hover:-translate-y-0.5">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={onSubmit} disabled={loading} data-testid="quote-form-submit" className="btn-pill bg-signal hover:bg-signalhover disabled:opacity-60 text-white px-8 py-3.5 hover:-translate-y-0.5 hover:shadow-glow">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Sending…" : "Send quote request"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ============ Contact form ============ */

export function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const { loading, done, submit } = useSubmit("/contact", "Message");
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit(f);
  };

  if (done) {
    return <SuccessCard done={done} title="Message sent" body="Our team will get back to you shortly — usually within a couple of hours during business days." />;
  }

  return (
    <form onSubmit={onSubmit} className="card p-6 sm:p-10" data-testid="contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full name" required testid="contact-name">
          <input data-testid="contact-name-input" required className={inputCls} placeholder="Jordan Alvarez" value={f.name} onChange={set("name")} />
        </Field>
        <Field label="Company" testid="contact-company">
          <input data-testid="contact-company-input" className={inputCls} placeholder="Valley Foods Inc." value={f.company} onChange={set("company")} />
        </Field>
        <Field label="Email" required testid="contact-email">
          <input data-testid="contact-email-input" type="email" required className={inputCls} placeholder="you@company.com" value={f.email} onChange={set("email")} />
        </Field>
        <Field label="Phone" testid="contact-phone">
          <input data-testid="contact-phone-input" type="tel" className={inputCls} placeholder="(555) 555-5555" value={f.phone} onChange={set("phone")} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="How can we help?" required testid="contact-message">
            <textarea data-testid="contact-message-input" required rows={5} className={`${inputCls} resize-y`} placeholder="Tell us about your lanes, volumes or the load you need moved…" value={f.message} onChange={set("message")} />
          </Field>
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="contact-form-submit" className="mt-6 btn-pill bg-signal hover:bg-signalhover disabled:opacity-60 text-white px-8 py-3.5 hover:-translate-y-0.5 hover:shadow-glow">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}

/* ============ Carrier application ============ */

export function CarrierForm() {
  const [f, setF] = useState({ contact_name: "", company: "", mc_dot: "", email: "", phone: "", equipment: "", message: "" });
  const { loading, done, submit } = useSubmit("/carriers", "Carrier");
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit(f);
  };

  if (done) {
    return <SuccessCard done={done} title="Application received" body="Our carrier team will review your details and reach out — usually within one business day." />;
  }

  return (
    <form onSubmit={onSubmit} className="card p-6 sm:p-10" data-testid="carrier-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Contact name" required testid="carrier-name">
          <input data-testid="carrier-name-input" required className={inputCls} placeholder="Luis Garcia" value={f.contact_name} onChange={set("contact_name")} />
        </Field>
        <Field label="Company name" required testid="carrier-company">
          <input data-testid="carrier-company-input" required className={inputCls} placeholder="Garcia Transport LLC" value={f.company} onChange={set("company")} />
        </Field>
        <Field label="MC / DOT number" testid="carrier-mc">
          <input data-testid="carrier-mc-input" className={inputCls} placeholder="MC-123456" value={f.mc_dot} onChange={set("mc_dot")} />
        </Field>
        <Field label="Equipment type" testid="carrier-equipment">
          <input data-testid="carrier-equipment-input" className={inputCls} placeholder="53' Dry Van, Reefer, Flatbed…" value={f.equipment} onChange={set("equipment")} />
        </Field>
        <Field label="Email" required testid="carrier-email">
          <input data-testid="carrier-email-input" type="email" required className={inputCls} placeholder="dispatch@company.com" value={f.email} onChange={set("email")} />
        </Field>
        <Field label="Phone" required testid="carrier-phone">
          <input data-testid="carrier-phone-input" type="tel" required className={inputCls} placeholder="(555) 555-5555" value={f.phone} onChange={set("phone")} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Lanes you run" testid="carrier-message">
            <textarea data-testid="carrier-message-input" rows={3} className={`${inputCls} resize-y`} placeholder="e.g. CA → AZ → NV, home weekly…" value={f.message} onChange={set("message")} />
          </Field>
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="carrier-form-submit" className="mt-6 btn-pill bg-signal hover:bg-signalhover disabled:opacity-60 text-white px-8 py-3.5 hover:-translate-y-0.5 hover:shadow-glow">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? "Sending…" : "Apply to the network"}
      </button>
    </form>
  );
}

/* ============ Agent application ============ */

export function AgentForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", years_experience: "", message: "" });
  const { loading, done, submit } = useSubmit("/agents", "Agent");
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit(f);
  };

  if (done) {
    return <SuccessCard done={done} title="Application received" body="Thanks for your interest in the Ferry Freight agent program. We'll be in touch within one business day." />;
  }

  return (
    <form onSubmit={onSubmit} className="card p-6 sm:p-10" data-testid="agent-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full name" required testid="agent-name">
          <input data-testid="agent-name-input" required className={inputCls} placeholder="Sam Rivera" value={f.name} onChange={set("name")} />
        </Field>
        <Field label="Years of experience" testid="agent-experience">
          <input data-testid="agent-experience-input" className={inputCls} placeholder="8" value={f.years_experience} onChange={set("years_experience")} />
        </Field>
        <Field label="Email" required testid="agent-email">
          <input data-testid="agent-email-input" type="email" required className={inputCls} placeholder="you@email.com" value={f.email} onChange={set("email")} />
        </Field>
        <Field label="Phone" required testid="agent-phone">
          <input data-testid="agent-phone-input" type="tel" required className={inputCls} placeholder="(555) 555-5555" value={f.phone} onChange={set("phone")} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Tell us about your book" testid="agent-message">
            <textarea data-testid="agent-message-input" rows={4} className={`${inputCls} resize-y`} placeholder="Lanes, verticals, average monthly volume…" value={f.message} onChange={set("message")} />
          </Field>
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="agent-form-submit" className="mt-6 btn-pill bg-signal hover:bg-signalhover disabled:opacity-60 text-white px-8 py-3.5 hover:-translate-y-0.5 hover:shadow-glow">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? "Sending…" : "Apply as an agent"}
      </button>
    </form>
  );
}
